import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Activity, RefreshCw, TrendingUp, Users, MousePointerClick, Zap, CheckCircle, Euro,
  ArrowDown, Filter, BarChart3, Radio,
} from 'lucide-react';
import { toast } from 'sonner';
import { TrafficSummary } from '../../components/admin/TrafficSummary';

const fmtNl = (n) => new Intl.NumberFormat('nl-NL').format(Number(n || 0));
const fmtPct = (n) => `${(Number(n) || 0).toFixed(1)}%`;
const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit', day: '2-digit', month: 'short' });
  } catch { return iso; }
};

const KIND_LABEL = {
  scan_start: 'Scan gestart',
  scan_complete: 'Scan voltooid',
  register: 'Registratie',
  checkout_started: 'Checkout gestart',
  paid: 'Betaald',
  lead_submit: 'Lead ingediend',
  page_view_high_intent: 'Hoog-intent view',
};
const KIND_COLOR = {
  scan_start: '#94a3b8',
  scan_complete: '#0ea5e9',
  register: '#16a34a',
  checkout_started: '#f59e0b',
  paid: '#D4A017',
  lead_submit: '#a855f7',
  page_view_high_intent: '#64748b',
};

export default function TrafficPage() {
  const { api } = useAuth();
  const [days, setDays] = useState(30);
  const [utmFilter, setUtmFilter] = useState('');
  const [funnel, setFunnel] = useState(null);
  const [utm, setUtm] = useState(null);
  const [daily, setDaily] = useState(null);
  const [live, setLive] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    else setRefreshing(true);
    setError(false);
    try {
      const qs = utmFilter ? `&utm_source=${encodeURIComponent(utmFilter)}` : '';
      const [fR, uR, dR, lR] = await Promise.all([
        api('get', `/api/admin/traffic/funnel?days=${days}${qs}`),
        api('get', `/api/admin/traffic/utm?days=${days}`),
        api('get', `/api/admin/traffic/daily?days=${days}`),
        api('get', `/api/admin/traffic/live?limit=30`),
      ]);
      setFunnel(fR.data);
      setUtm(uR.data);
      setDaily(dR.data);
      setLive(lR.data?.events || []);
      setLastUpdated(new Date());
    } catch (e) {
      setError(true);
      toast.error('Fout bij laden traffic data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [api, days, utmFilter]);

  useEffect(() => { load(true); }, [load]);

  // Auto-refresh elke 15s
  useEffect(() => {
    const id = setInterval(() => load(false), 15000);
    return () => clearInterval(id);
  }, [load]);

  const handleBackfill = async () => {
    setBackfilling(true);
    try {
      const r = await api('post', '/api/admin/traffic/backfill');
      toast.success(`${r.data.backfilled_events} events aangemaakt vanuit bestaande data`);
      load(true);
    } catch (e) {
      toast.error('Backfill mislukt');
    } finally {
      setBackfilling(false);
    }
  };

  const utmSources = useMemo(() => {
    const set = new Set();
    (utm?.rows || []).forEach(r => { if (r.source) set.add(r.source); });
    return Array.from(set).sort();
  }, [utm]);

  // Funnel stage values
  const funnelStages = funnel ? [
    { key: 'visitors', label: 'Bezoekers (uniek)', value: funnel.visits?.unique_visitors || 0, color: '#0f172a', icon: Users, sub: `${fmtNl(funnel.visits?.total_pageviews || 0)} pageviews` },
    { key: 'register', label: 'Geregistreerd', value: funnel.register?.unique || 0, color: '#16a34a', icon: Users, rate: funnel.conversion_rates?.visit_to_register },
    { key: 'scan_start', label: 'Scan gestart', value: funnel.scan_start?.unique || 0, color: '#0ea5e9', icon: Zap, rate: funnel.conversion_rates?.register_to_scan_start },
    { key: 'scan_complete', label: 'Scan voltooid', value: funnel.scan_complete?.unique || 0, color: '#06b6d4', icon: CheckCircle, rate: funnel.conversion_rates?.scan_start_to_complete },
    { key: 'paid', label: 'Betaald', value: funnel.paid?.unique || 0, color: '#D4A017', icon: Euro, rate: funnel.conversion_rates?.scan_complete_to_paid },
  ] : [];

  const maxFunnel = Math.max(1, ...funnelStages.map(s => s.value));
  const maxDailyVisitors = Math.max(1, ...(daily?.series || []).map(d => d.visitors));

  return (
    <div data-testid="admin-traffic-page" style={{ maxWidth: '1300px', margin: '0 auto', paddingBottom: '40px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: '26px', fontWeight: 800, color: 'white', marginBottom: '4px' }}>
            Traffic & Funnel
          </h1>
          <p style={{ color: '#94a3b8', fontSize: '13px', margin: 0 }}>
            Bezoekers, UTM-bronnen en conversies
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {lastUpdated && (
            <span data-testid="traffic-last-updated" style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              background: 'rgba(34,197,94,0.12)', color: '#22c55e', borderRadius: '20px',
              padding: '5px 12px', fontSize: '11px', fontWeight: 700,
            }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#22c55e', animation: 'pulse 1.5s infinite' }} />
              Bijgewerkt {fmtTime(lastUpdated.toISOString())}
            </span>
          )}
          <button
            data-testid="traffic-refresh-btn"
            onClick={() => load(true)}
            disabled={refreshing || loading}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              background: '#D4A017', color: '#0f172a', border: 'none', borderRadius: '10px',
              padding: '8px 14px', fontSize: '12px', fontWeight: 700, cursor: 'pointer',
              opacity: (refreshing || loading) ? 0.6 : 1,
            }}
          >
            <RefreshCw size={13} style={{ animation: (refreshing || loading) ? 'spin 1s linear infinite' : 'none' }} /> Vernieuwen
          </button>
        </div>
      </div>

      <TrafficSummary />
      {/* Filter bar */}
      <div style={{ background: 'white', borderRadius: '12px', padding: '14px 18px', marginBottom: '18px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', border: '1px solid #f1f5f9' }}>
        <Filter size={15} style={{ color: '#94a3b8' }} />
        <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b' }}>Periode:</span>
        {[1, 2, 7, 30, 90].map(d => (
          <button
            key={d}
            data-testid={`traffic-days-${d}`}
            onClick={() => setDays(d)}
            style={{
              padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 700,
              background: days === d ? '#0f172a' : '#f1f5f9',
              color: days === d ? 'white' : '#64748b',
              border: 'none', cursor: 'pointer',
            }}
          >
            {d <= 2 ? `${d * 24}u` : `${d}d`}
          </button>
        ))}
        <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', marginLeft: '14px' }}>UTM source:</span>
        <select
          data-testid="traffic-utm-filter"
          value={utmFilter}
          onChange={(e) => setUtmFilter(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px', background: 'white' }}
        >
          <option value="">Alle bronnen</option>
          {utmSources.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
          <button
            data-testid="traffic-backfill-btn"
            onClick={handleBackfill}
            disabled={backfilling}
            style={{
              padding: '6px 12px', borderRadius: '8px', fontSize: '11px', fontWeight: 700,
              background: '#fef9e7', color: '#92400e', border: '1px solid #fde68a',
              cursor: 'pointer', opacity: backfilling ? 0.6 : 1,
            }}
            title="Maak track_events voor bestaande registraties + scans"
          >
            {backfilling ? 'Bezig...' : 'Backfill events'}
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ background: 'white', borderRadius: '14px', padding: '60px', textAlign: 'center', color: '#94a3b8' }}>Traffic data laden…</div>
      ) : error ? (
        <p data-testid="traffic-data-error" role="alert" className="text-amber-300 text-sm py-6">Het overzicht kon niet worden geladen. Vernieuw om opnieuw te proberen.</p>
      ) : (
        <>
          {/* Funnel cards */}
          <div data-testid="traffic-funnel" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px', marginBottom: '24px' }} className="traffic-funnel-grid">
            {funnelStages.map((s, idx) => {
              const Icon = s.icon;
              const widthPct = Math.max(8, Math.round((s.value / maxFunnel) * 100));
              return (
                <div key={s.key} data-testid={`funnel-${s.key}`} style={{
                  background: 'white', borderRadius: '14px', padding: '16px',
                  border: '1px solid #f1f5f9', position: 'relative',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: `${s.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon size={14} style={{ color: s.color }} />
                    </div>
                    <p style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, margin: 0 }}>
                      Stap {idx + 1}
                    </p>
                  </div>
                  <p style={{ fontFamily: "'Sora', sans-serif", fontSize: '26px', fontWeight: 800, color: s.color, margin: '4px 0', lineHeight: 1 }}>
                    {fmtNl(s.value)}
                  </p>
                  <p style={{ fontSize: '12px', color: '#0f172a', fontWeight: 600, margin: '4px 0 0' }}>{s.label}</p>
                  {s.sub && <p style={{ fontSize: '10px', color: '#94a3b8', margin: '2px 0 0' }}>{s.sub}</p>}
                  {s.rate != null && (
                    <p style={{ fontSize: '10px', color: s.color, margin: '6px 0 0', fontWeight: 700 }}>
                      <ArrowDown size={9} style={{ display: 'inline', verticalAlign: '-1px' }} /> {fmtPct(s.rate)} conversie
                    </p>
                  )}
                  <div style={{ marginTop: '10px', height: '4px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${widthPct}%`, background: s.color, transition: 'width 0.5s' }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Overall conversion */}
          {funnel && (
            <div data-testid="traffic-overall" style={{ background: 'linear-gradient(135deg, #0f172a, #1e293b)', borderRadius: '14px', padding: '20px 24px', marginBottom: '24px', color: 'white' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <p style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#94a3b8', fontWeight: 700, margin: 0 }}>
                    Eind-tot-eind conversie ({days}d{utmFilter ? ` · ${utmFilter}` : ''})
                  </p>
                  <p style={{ fontFamily: "'Sora', sans-serif", fontSize: '32px', fontWeight: 800, color: '#D4A017', margin: '4px 0 0', lineHeight: 1 }}>
                    {fmtPct(funnel.conversion_rates?.overall_visit_to_paid)}
                  </p>
                  <p style={{ fontSize: '12px', color: '#cbd5e1', margin: '4px 0 0' }}>
                    Gekoppelde bezoekers en betalende klanten binnen de periode
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                  <MiniStat label="Visit → Scan" value={fmtPct(funnel.conversion_rates?.visit_to_scan_start)} />
                  <MiniStat label="Register → Scan" value={fmtPct(funnel.conversion_rates?.register_to_scan_start)} />
                  <MiniStat label="Register → Paid" value={fmtPct(funnel.conversion_rates?.register_to_paid)} />
                </div>
              </div>
            </div>
          )}

          {/* Daily chart + Live ticker (side by side) */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: '16px', marginBottom: '24px' }} className="traffic-grid-2">
            {/* Chart */}
            <div data-testid="traffic-daily-chart" style={{ background: 'white', borderRadius: '14px', padding: '18px 20px', border: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <BarChart3 size={16} style={{ color: '#D4A017' }} />
                <h3 style={{ fontSize: '14px', fontWeight: 800, color: '#0f172a', margin: 0 }}>Dagelijks verkeer</h3>
                <span style={{ fontSize: '11px', color: '#94a3b8' }}>· laatste {days} dagen</span>
              </div>
              {(daily?.series || []).length === 0 ? (
                <p style={{ color: '#94a3b8', fontSize: '13px', textAlign: 'center', padding: '40px 0' }}>Nog geen data — wacht tot de eerste bezoekers binnenkomen.</p>
              ) : (
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: '180px', borderBottom: '1px solid #f1f5f9', paddingBottom: '4px' }}>
                  {(daily.series || []).slice(-30).map((d) => {
                    const h = Math.max(2, Math.round((d.visitors / maxDailyVisitors) * 160));
                    const hReg = Math.round((d.registers / maxDailyVisitors) * 160);
                    return (
                      <div key={d.date} title={`${d.date}: ${d.visitors} bezoekers · ${d.registers} reg · ${d.paid} paid`}
                        style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: '1px', cursor: 'help' }}>
                        <div style={{ width: '100%', height: `${h}px`, background: 'linear-gradient(180deg, #D4A017, #92400e)', borderRadius: '3px 3px 0 0', position: 'relative' }}>
                          {hReg > 0 && (
                            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: `${Math.max(2, hReg)}px`, background: '#16a34a', borderRadius: '0 0 3px 3px' }} />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div style={{ display: 'flex', gap: '14px', marginTop: '10px', fontSize: '11px', color: '#64748b' }}>
                <span><span style={{ display: 'inline-block', width: '10px', height: '10px', background: '#D4A017', borderRadius: '2px', marginRight: '5px', verticalAlign: 'middle' }} />Bezoekers</span>
                <span><span style={{ display: 'inline-block', width: '10px', height: '10px', background: '#16a34a', borderRadius: '2px', marginRight: '5px', verticalAlign: 'middle' }} />Registraties</span>
              </div>
            </div>

            {/* Live ticker */}
            <div data-testid="traffic-live-ticker" style={{ background: 'linear-gradient(135deg, #0f172a, #1e1b4b)', borderRadius: '14px', padding: '18px 20px', color: 'white', maxHeight: '480px', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                <Radio size={16} style={{ color: '#D4A017' }} />
                <h3 style={{ fontSize: '14px', fontWeight: 800, margin: 0 }}>Live ticker</h3>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: 'rgba(34,197,94,0.15)', color: '#22c55e', borderRadius: '20px', padding: '2px 8px', fontSize: '10px', fontWeight: 700, marginLeft: 'auto' }}>
                  <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#22c55e', animation: 'pulse 1.5s infinite' }} />
                  LIVE
                </span>
              </div>
              {live.length === 0 ? (
                <p style={{ color: '#cbd5e1', fontSize: '12px', padding: '20px 0' }}>Nog geen events — events verschijnen hier real-time.</p>
              ) : (
                <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {live.map((ev, idx) => {
                    const color = KIND_COLOR[ev.kind] || '#94a3b8';
                    return (
                      <div key={idx} style={{
                        display: 'flex', alignItems: 'flex-start', gap: '8px',
                        padding: '8px 10px', background: 'rgba(255,255,255,0.04)', borderRadius: '8px',
                        borderLeft: `3px solid ${color}`,
                      }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <p style={{ fontSize: '11px', fontWeight: 700, color, margin: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            {KIND_LABEL[ev.kind] || ev.kind}
                          </p>
                          <p style={{ fontSize: '11px', color: '#cbd5e1', margin: '2px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {ev.email || ev.company_name || ev.path || `visitor ${ev.visitor_id}`}
                          </p>
                          {ev.utm_source && (
                            <p style={{ fontSize: '10px', color: '#94a3b8', margin: '2px 0 0' }}>via {ev.utm_source}</p>
                          )}
                        </div>
                        <span style={{ fontSize: '10px', color: '#94a3b8', whiteSpace: 'nowrap' }}>{fmtTime(ev.ts)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* UTM attributie tabel */}
          <div data-testid="traffic-utm-table" style={{ background: 'white', borderRadius: '14px', padding: '18px 20px', border: '1px solid #f1f5f9' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
              <TrendingUp size={16} style={{ color: '#D4A017' }} />
              <h3 style={{ fontSize: '14px', fontWeight: 800, color: '#0f172a', margin: 0 }}>UTM-bron attributie</h3>
              <span style={{ fontSize: '11px', color: '#94a3b8' }}>· {utm?.rows?.length || 0} bronnen</span>
            </div>
            {(utm?.rows || []).length === 0 ? (
              <p style={{ color: '#94a3b8', fontSize: '13px', textAlign: 'center', padding: '40px 0' }}>
                Geen UTM-data. Voeg <code>?utm_source=...</code> toe aan campagne-links om bronnen te tracken.
              </p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="traffic-utm-data" style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', overflowWrap: 'anywhere' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Bron</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Campagne</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Visitors</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Scans</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Registers</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Paid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(utm.rows || []).map((r, idx) => {
                      return (
                        <tr key={idx} style={{ borderBottom: '1px solid #f8fafc' }}>
                          <td data-testid={`utm-source-${idx}`} data-label="Bron" style={{ padding: '12px', fontSize: '13px', color: '#0f172a', fontWeight: 600 }}>{r.source || 'direct'}</td>
                          <td data-testid={`utm-campaign-${idx}`} data-label="Campagne" style={{ padding: '12px', fontSize: '12px', color: '#64748b' }}>{r.campaign || '—'}</td>
                          <td data-testid={`utm-visitors-${idx}`} data-label="Bezoekers" style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>{fmtNl(r.visitors)}</td>
                          <td data-testid={`utm-scans-${idx}`} data-label="Scans" style={{ padding: '12px', textAlign: 'right', fontSize: '13px', color: '#0ea5e9', fontWeight: 600 }}>{fmtNl(r.scan_completes)}</td>
                          <td data-testid={`utm-registers-${idx}`} data-label="Registraties" style={{ padding: '12px', textAlign: 'right', fontSize: '13px', color: '#16a34a', fontWeight: 600 }}>{fmtNl(r.registers)}</td>
                          <td data-testid={`utm-paid-${idx}`} data-label="Betaald" style={{ padding: '12px', textAlign: 'right', fontSize: '13px', color: '#D4A017', fontWeight: 700 }}>{fmtNl(r.paid)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      <style>{`
        @keyframes pulse { 0% { opacity: 1; } 50% { opacity: 0.4; } 100% { opacity: 1; } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @media (max-width: 980px) {
          .traffic-funnel-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .traffic-grid-2 { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 640px) {
          .traffic-utm-data, .traffic-utm-data tbody { display: block; width: 100%; }
          .traffic-utm-data thead { display: none; }
          .traffic-utm-data tr { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); width: 100%; }
          .traffic-utm-data td { display: block; min-width: 0; text-align: left !important; }
          .traffic-utm-data td:first-child, .traffic-utm-data td:nth-child(2) { grid-column: 1 / -1; }
          .traffic-utm-data td::before { content: attr(data-label); display: block; margin-bottom: 5px; color: #64748b; font-size: 10px; font-weight: 500; }
        }
      `}</style>
    </div>
  );
}

const MiniStat = ({ label, value }) => (
  <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: '10px', padding: '10px 14px', minWidth: '110px' }}>
    <p style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, margin: 0 }}>{label}</p>
    <p style={{ fontSize: '18px', fontWeight: 800, color: 'white', margin: '4px 0 0', lineHeight: 1 }}>{value}</p>
  </div>
);
